import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchSpeechToTextSetting, fetchAdminNotificationSettings } from '../services/api';
import { useAuth } from './AuthContext';
import { NotificationSettings } from '../types';

interface SettingsContextType {
    speechToTextEnabled: boolean;
    notificationsEnabled: boolean;
    loading: boolean;
    error: string | null;
    refreshSettings: () => Promise<void>;
    updateSpeechToTextEnabled: (enabled: boolean) => void;
    updateNotificationsEnabled: (enabled: boolean) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

interface SettingsProviderProps {
    children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
    const { user } = useAuth();
    const [speechToTextEnabled, setSpeechToTextEnabled] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refreshSettings = async () => {
        try {
            setLoading(true);
            setError(null);

            // Load speech-to-text setting (admin only; public endpoint returns 403 for non-admin)
            if (user?.role === 'admin') {
                try {
                    const speechRes = await fetchSpeechToTextSetting();
                    setSpeechToTextEnabled(speechRes?.enabled || false);
                } catch (e) {
                    setSpeechToTextEnabled(false);
                }
            } else {
                setSpeechToTextEnabled(false);
            }

            // Load notification settings (admin only)
            if (user?.role === 'admin') {
                try {
                    const notificationRes = await fetchAdminNotificationSettings();
                    const settings = notificationRes?.data as NotificationSettings;
                    setNotificationsEnabled(settings?.enabled || true);
                } catch (e) {
                    // If notification settings fail, default to enabled
                    setNotificationsEnabled(true);
                }
            } else {
                // Non-admin users use safe default for notifications
                setNotificationsEnabled(true);
            }
        } catch (e: any) {
            setError(e?.message || "Failed to load settings");
            setSpeechToTextEnabled(false); // Default to disabled on error
            setNotificationsEnabled(true); // Default to enabled on error
        } finally {
            setLoading(false);
        }
    };

    const updateSpeechToTextEnabled = (enabled: boolean) => {
        setSpeechToTextEnabled(enabled);
    };

    const updateNotificationsEnabled = (enabled: boolean) => {
        setNotificationsEnabled(enabled);
    };

    useEffect(() => {
        refreshSettings();
        // Re-run when user changes to respect role
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const value: SettingsContextType = {
        speechToTextEnabled,
        notificationsEnabled,
        loading,
        error,
        refreshSettings,
        updateSpeechToTextEnabled,
        updateNotificationsEnabled,
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};
